import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Image,
  Alert,
  Modal,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import LottieView from "lottie-react-native";
import DateTimePicker from "@react-native-community/datetimepicker";
import { LocationPicker } from "@/modules/agenda/components/LocationPicker";
import { InstructieItem, CompetitionSettings } from "@/types";
import { getAllLotties, getLottiesByCategory } from "@/lib/lottie-registry";
import { createFamilyChat } from "@/modules/chat/utils/createFamilyChat";

// Available Lottie animations for banner (celebration category is most appropriate)
const LOTTIE_OPTIONS = getAllLotties()
  .filter((l) => ["celebration", "object", "action"].includes(l.category))
  .map((l) => ({ id: l.key, name: l.name, file: l.source }));

// Color options for banner border
const COLOR_OPTIONS = [
  { id: "#7C3AED", name: "Paars" },
  { id: "#EF4444", name: "Rood" },
  { id: "#F97316", name: "Oranje" },
  { id: "#FBBF24", name: "Geel" },
  { id: "#22C55E", name: "Groen" },
  { id: "#06B6D4", name: "Cyaan" },
  { id: "#3B82F6", name: "Blauw" },
  { id: "#EC4899", name: "Roze" },
  { id: "#8B5CF6", name: "Violet" },
  { id: "#6366F1", name: "Indigo" },
];

interface FamilieAdmin {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
}

/**
 * Algemeen Admin Screen - Weekend settings for familie admins
 */
export default function AlgemeenScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { activeWeekend, refreshProfile, roles, user } = useAuth();

  // Form state - Weekend
  const [name, setName] = useState("");
  const [theme, setTheme] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [locationName, setLocationName] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  const [locationLat, setLocationLat] = useState<number | null>(null);
  const [locationLng, setLocationLng] = useState<number | null>(null);
  const [bannerColor, setBannerColor] = useState("#7C3AED");
  const [lottieAnimation, setLottieAnimation] = useState("confetti");
  const [weekendLocationRevealed, setWeekendLocationRevealed] = useState(false);
  const [weekendInstructies, setWeekendInstructies] = useState<InstructieItem[]>([]);

  // Form state - Familie Dag
  const [familieDagDate, setFamilieDagDate] = useState<Date | null>(null);
  const [familieDagLocationName, setFamilieDagLocationName] = useState("");
  const [familieDagLocationAddress, setFamilieDagLocationAddress] = useState("");
  const [familieDagLocationLat, setFamilieDagLocationLat] = useState<number | null>(null);
  const [familieDagLocationLng, setFamilieDagLocationLng] = useState<number | null>(null);
  const [familieDagInstructies, setFamilieDagInstructies] = useState<InstructieItem[]>([]);

  // Legacy field - keep for backwards compatibility
  const [familieDagLocation, setFamilieDagLocation] = useState("");
  const [familieDagLocationRevealed, setFamilieDagLocationRevealed] = useState(false);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [familieAdmins, setFamilieAdmins] = useState<FamilieAdmin[]>([]);
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);
  const [showFamilieDagPicker, setShowFamilieDagPicker] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Competition settings state
  const [competitionMode, setCompetitionMode] = useState<"global" | "per_game">("global");
  const [competitionStartsAt, setCompetitionStartsAt] = useState<Date | null>(null);
  const [competitionEndsAt, setCompetitionEndsAt] = useState<Date | null>(null);
  const [showLeaderboard, setShowLeaderboard] = useState(true);
  const [showCompetitionStartPicker, setShowCompetitionStartPicker] = useState(false);
  const [showCompetitionEndPicker, setShowCompetitionEndPicker] = useState(false);
  const [androidTimePickerTarget, setAndroidTimePickerTarget] = useState<"start" | "end" | null>(null);
  const [androidTempDate, setAndroidTempDate] = useState<Date | null>(null);

  // Instructies modal state
  const [showInstructiesModal, setShowInstructiesModal] = useState(false);
  const [editingInstructiesType, setEditingInstructiesType] = useState<"weekend" | "familiedag">("weekend");
  const [editingInstructie, setEditingInstructie] = useState<InstructieItem | null>(null);
  const [instructieTitle, setInstructieTitle] = useState("");
  const [instructieDescription, setInstructieDescription] = useState("");
  const [instructieIcon, setInstructieIcon] = useState("information-circle");

  // Family chat state
  const [familyChatId, setFamilyChatId] = useState<string | null>(null);
  const [familyChatParticipantCount, setFamilyChatParticipantCount] = useState(0);
  const [isCreatingChat, setIsCreatingChat] = useState(false);

  /**
   * Fetch current weekend data and familie admins
   */
  const fetchData = useCallback(async () => {
    if (!activeWeekend?.id) {
      setIsLoading(false);
      return;
    }

    try {
      // Fetch weekend details
      const { data: weekend, error: weekendError } = await supabase
        .from("family_weekends")
        .select("*")
        .eq("id", activeWeekend.id)
        .single();

      if (weekendError) throw weekendError;

      if (weekend) {
        setName(weekend.name || "");
        setTheme(weekend.theme || "");
        setStartDate(weekend.start_date ? new Date(weekend.start_date) : null);
        setEndDate(weekend.end_date ? new Date(weekend.end_date) : null);
        
        // Weekend location
        setLocationName(weekend.location_name || "");
        setLocationAddress(weekend.location_address || "");
        setLocationLat(weekend.location_lat || null);
        setLocationLng(weekend.location_lng || null);
        setWeekendLocationRevealed(weekend.weekend_location_revealed || false);
        setWeekendInstructies(weekend.weekend_instructies || []);
        
        // Banner
        setBannerColor(weekend.banner_color || "#7C3AED");
        setLottieAnimation(weekend.lottie_animation || "confetti");
        
        // Familie Dag
        setFamilieDagDate(weekend.familie_dag_date ? new Date(weekend.familie_dag_date) : null);
        setFamilieDagLocationName(weekend.familie_dag_location || "");
        setFamilieDagLocationAddress(weekend.familie_dag_location_address || "");
        setFamilieDagLocationLat(weekend.familie_dag_location_lat || null);
        setFamilieDagLocationLng(weekend.familie_dag_location_lng || null);
        setFamilieDagLocationRevealed(weekend.familie_dag_location_revealed || false);
        setFamilieDagInstructies(weekend.familie_dag_instructies || []);
        
        // Legacy
        setFamilieDagLocation(weekend.familie_dag_location || "");

        // Competition settings
        const compSettings = weekend.competition_settings as CompetitionSettings | null;
        if (compSettings) {
          setCompetitionMode(compSettings.mode || "global");
          setCompetitionStartsAt(compSettings.global_starts_at ? new Date(compSettings.global_starts_at) : null);
          setCompetitionEndsAt(compSettings.global_ends_at ? new Date(compSettings.global_ends_at) : null);
          setShowLeaderboard(compSettings.show_leaderboard !== false);
        }
      }

      // Fetch familie admins
      const { data: admins, error: adminsError } = await supabase
        .from("user_roles")
        .select(`
          user_id,
          profiles:user_id (
            id,
            full_name,
            email,
            avatar_url
          )
        `)
        .eq("role", "familie_admin")
        .is("revoked_at", null);

      if (adminsError) throw adminsError;

      const adminList: FamilieAdmin[] = (admins || [])
        .map((a: any) => a.profiles)
        .filter(Boolean);
      setFamilieAdmins(adminList);

      // Check if family chat exists for this weekend
      const { data: existingChat, error: chatError } = await supabase
        .from("chats")
        .select(`
          id,
          participants:chat_participants(count)
        `)
        .eq("weekend_id", activeWeekend.id)
        .eq("type", "family")
        .maybeSingle();

      if (!chatError && existingChat) {
        setFamilyChatId(existingChat.id);
        const participantCount = (existingChat.participants as any)?.[0]?.count || 0;
        setFamilyChatParticipantCount(participantCount);
      } else {
        setFamilyChatId(null);
        setFamilyChatParticipantCount(0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      Alert.alert("Fout", "Kon gegevens niet laden");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [activeWeekend?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Handle refresh
   */
  const onRefresh = useCallback(() => {
    setIsRefreshing(true);
    fetchData();
  }, [fetchData]);

  /**
   * Handle creating the family chat
   */
  const handleCreateFamilyChat = async () => {
    if (!activeWeekend?.id || !user?.id) {
      Alert.alert("Fout", "Geen actief weekend of gebruiker gevonden");
      return;
    }

    Alert.alert(
      "Familie Chat Aanmaken",
      "Dit maakt een groepschat aan voor alle familieleden. Alle gebruikers met een account worden automatisch toegevoegd.",
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Aanmaken",
          onPress: async () => {
            setIsCreatingChat(true);
            try {
              const chatId = await createFamilyChat({
                weekendId: activeWeekend.id,
                weekendName: activeWeekend.name || `Weekend ${activeWeekend.year}`,
                createdBy: user.id,
              });

              if (chatId) {
                setFamilyChatId(chatId);
                // Fetch the participant count
                const { data: chatData } = await supabase
                  .from("chats")
                  .select(`participants:chat_participants(count)`)
                  .eq("id", chatId)
                  .single();

                const count = (chatData?.participants as any)?.[0]?.count || 0;
                setFamilyChatParticipantCount(count);

                Alert.alert(
                  "Succes! 🎉",
                  `De familie chat is aangemaakt met ${count} deelnemers. Iedereen kan nu chatten!`
                );
              } else {
                Alert.alert("Fout", "Kon de familie chat niet aanmaken");
              }
            } catch (error) {
              console.error("Error creating family chat:", error);
              Alert.alert("Fout", "Er is iets misgegaan bij het aanmaken van de chat");
            } finally {
              setIsCreatingChat(false);
            }
          },
        },
      ]
    );
  };

  /**
   * Navigate to family chat
   */
  const handleOpenFamilyChat = () => {
    if (familyChatId) {
      router.push(`/chat-screens/${familyChatId}` as any);
    }
  };

  /**
   * Create or update calendar event for Familie Dag/Weekend
   */
  const upsertCalendarEvent = async (
    category: "family_day" | "family_weekend",
    title: string,
    startDate: Date,
    endDate: Date | null,
    location: string | null,
    locationRevealed: boolean
  ) => {
    if (!activeWeekend?.id || !roles.isFamilieAdmin) return;

    try {
      // Check if event already exists
      const { data: existingEvent } = await supabase
        .from("agenda_events")
        .select("id")
        .eq("weekend_id", activeWeekend.id)
        .eq("category", category)
        .maybeSingle();

      const eventData = {
        weekend_id: activeWeekend.id,
        title,
        description: category === "family_day" 
          ? "De jaarlijkse Familie Dag" 
          : "Het jaarlijkse Familie Weekend",
        location: locationRevealed ? location : null,
        starts_at: startDate.toISOString(),
        ends_at: (endDate || startDate).toISOString(),
        all_day: true,
        category,
        icon: category === "family_day" ? "sunny" : "bed",
        color: category === "family_day" ? "#F59E0B" : "#10B981",
        is_mandatory: false,
        send_reminder: true,
        reminder_minutes_before: 60,
        event_type: "family",
        visibility: "public",
        has_reveal: !locationRevealed && !!location,
        reveal_target: "location",
        is_revealed: locationRevealed,
        secret_title: null,
        secret_description: locationRevealed ? null : "De locatie wordt binnenkort onthuld!",
      };

      if (existingEvent?.id) {
        // Update existing event
        await supabase
          .from("agenda_events")
          .update({
            ...eventData,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingEvent.id);
      } else {
        // Create new event
        await supabase.from("agenda_events").insert({
          ...eventData,
          created_by: roles.isFamilieAdmin ? (await supabase.auth.getUser()).data.user?.id : null,
        });
      }
    } catch (err) {
      console.error(`Error upserting ${category} calendar event:`, err);
    }
  };

  /**
   * Save changes
   */
  const handleSave = async () => {
    if (!activeWeekend?.id) return;

    if (!name.trim()) {
      Alert.alert("Fout", "Naam is verplicht");
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("family_weekends")
        .update({
          name: name.trim(),
          theme: theme.trim() || null,
          start_date: startDate?.toISOString().split("T")[0] || null,
          end_date: endDate?.toISOString().split("T")[0] || null,
          // Weekend location
          location_name: locationName.trim() || null,
          location_address: locationAddress.trim() || null,
          location_lat: locationLat,
          location_lng: locationLng,
          weekend_location_revealed: weekendLocationRevealed,
          weekend_instructies: weekendInstructies.length > 0 ? weekendInstructies : null,
          // Banner
          banner_color: bannerColor,
          lottie_animation: lottieAnimation,
          // Familie Dag
          familie_dag_date: familieDagDate?.toISOString().split("T")[0] || null,
          familie_dag_location: familieDagLocationName.trim() || null,
          familie_dag_location_address: familieDagLocationAddress.trim() || null,
          familie_dag_location_lat: familieDagLocationLat,
          familie_dag_location_lng: familieDagLocationLng,
          familie_dag_location_revealed: familieDagLocationRevealed,
          familie_dag_instructies: familieDagInstructies.length > 0 ? familieDagInstructies : null,
          // Competition settings - always use global mode with optional per-game overrides
          competition_settings: {
            mode: "global",
            global_starts_at: competitionStartsAt?.toISOString() || null,
            global_ends_at: competitionEndsAt?.toISOString() || null,
            show_leaderboard: showLeaderboard,
            leaderboard_scope: "all",
          } as CompetitionSettings,
        })
        .eq("id", activeWeekend.id);

      if (error) throw error;

      // Auto-create/update calendar events
      if (familieDagDate) {
        await upsertCalendarEvent(
          "family_day",
          "Familie Dag",
          familieDagDate,
          familieDagDate,
          familieDagLocationAddress.trim() || familieDagLocationName.trim() || null,
          familieDagLocationRevealed
        );
      }

      if (startDate) {
        await upsertCalendarEvent(
          "family_weekend",
          name.trim() || "Familie Weekend",
          startDate,
          endDate,
          locationAddress.trim() || locationName.trim() || null,
          weekendLocationRevealed
        );
      }

      await refreshProfile();
      setHasChanges(false);
      
      // Show success message and navigate back
      Alert.alert("Opgeslagen", "De instellingen zijn bijgewerkt", [
        {
          text: "OK",
          onPress: () => router.back(),
        },
      ]);
    } catch (error) {
      console.error("Error saving:", error);
      Alert.alert("Fout", "Kon instellingen niet opslaan");
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Get Lottie source for selected animation
   */
  const getSelectedLottie = () => {
    return LOTTIE_OPTIONS.find((l) => l.id === lottieAnimation)?.file || LOTTIE_OPTIONS[0].file;
  };

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <ActivityIndicator size="large" color={colors.primary} />
        <Text className="mt-4" style={{ color: colors.textSecondary }}>
          Laden...
        </Text>
      </SafeAreaView>
    );
  }

  if (!activeWeekend) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center px-6"
        style={{ backgroundColor: colors.background }}
      >
        <Ionicons name="calendar-outline" size={64} color={colors.textTertiary} />
        <Text className="text-lg font-medium mt-4 text-center" style={{ color: colors.text }}>
          Geen actief weekend
        </Text>
        <Text className="mt-2 text-center" style={{ color: colors.textSecondary }}>
          Er moet eerst een weekend actief zijn om instellingen te beheren.
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["bottom"]}>
      <ScrollView
        className="flex-1"
        contentContainerClassName="px-4 py-6"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={onRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Banner Preview */}
        <View className="mb-6">
          <Text className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: colors.textTertiary }}>
            Banner Voorbeeld
          </Text>
          <View
            className="rounded-2xl p-4 items-center"
            style={{
              backgroundColor: colors.surface,
              borderWidth: 3,
              borderColor: bannerColor,
            }}
          >
            <LottieView
              source={getSelectedLottie()}
              autoPlay
              loop
              style={{ width: 120, height: 120 }}
            />
            <Text className="text-xl font-bold text-center mt-2" style={{ color: colors.text }}>
              {name || "Weekend Naam"}
            </Text>
            {startDate && endDate && (
              <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                {format(startDate, "d MMM", { locale: nl })} - {format(endDate, "d MMM yyyy", { locale: nl })}
              </Text>
            )}
            {theme && (
              <View
                className="mt-2 px-3 py-1 rounded-full"
                style={{ backgroundColor: `${bannerColor}20` }}
              >
                <Text className="text-sm font-medium" style={{ color: bannerColor }}>
                  {theme}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Familie Weekend Section */}
        <View
          className="mb-6 p-4 rounded-2xl"
          style={{
            backgroundColor: colors.surface,
            borderWidth: 2,
            borderColor: "#10B981",
          }}
        >
          {/* Section Header */}
          <View className="flex-row items-center mb-4">
            <View
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: "#10B98120" }}
            >
              <Ionicons name="bed" size={20} color="#10B981" />
            </View>
            <View>
              <Text className="text-lg font-bold" style={{ color: colors.text }}>
                Familie Weekend
              </Text>
              <Text className="text-xs" style={{ color: colors.textSecondary }}>
                Het jaarlijkse meerdaagse weekend
              </Text>
            </View>
          </View>

          {/* Name */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Naam *
            </Text>
            <TextInput
              value={name}
              onChangeText={(text) => {
                setName(text);
                setHasChanges(true);
              }}
              placeholder="bijv. Staatsen 2026"
              placeholderTextColor={colors.textTertiary}
              className="p-4 rounded-xl text-base"
              style={{
                backgroundColor: colors.background,
                color: colors.text,
                borderWidth: 1,
                borderColor: colors.border,
              }}
            />
          </View>

          {/* Theme */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Thema
            </Text>
            <TextInput
              value={theme}
              onChangeText={(text) => {
                setTheme(text);
                setHasChanges(true);
              }}
              placeholder="bijv. Tropical Paradise"
              placeholderTextColor={colors.textTertiary}
              className="p-4 rounded-xl text-base"
              style={{
                backgroundColor: colors.background,
                color: colors.text,
                borderWidth: 1,
                borderColor: colors.border,
              }}
            />
          </View>

          {/* Weekend Dates Header */}
          <View className="flex-row items-center mb-3">
            <Ionicons name="calendar" size={16} color="#10B981" />
            <Text className="ml-2 text-sm font-semibold" style={{ color: "#10B981" }}>
              Familie Weekend Datum
            </Text>
          </View>

          {/* Dates */}
          <View className="flex-row mb-4">
            <View className="flex-1 mr-2">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Startdatum
              </Text>
              <TouchableOpacity
                onPress={() => setShowStartPicker(true)}
                className="p-4 rounded-xl flex-row items-center justify-between"
                style={{
                  backgroundColor: colors.background,
                  borderWidth: 1,
                  borderColor: colors.border,
                }}
              >
                <Text style={{ color: startDate ? colors.text : colors.textTertiary }}>
                  {startDate ? format(startDate, "d MMM yyyy", { locale: nl }) : "Selecteer"}
                </Text>
                <Ionicons name="calendar-outline" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <View className="flex-1 ml-2">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Einddatum
              </Text>
              <TouchableOpacity
                onPress={() => setShowEndPicker(true)}
                className="p-4 rounded-xl flex-row items-center justify-between"
                style={{
                  backgroundColor: colors.background,
                  borderWidth: 1,
                  borderColor: colors.border,
                }}
              >
                <Text style={{ color: endDate ? colors.text : colors.textTertiary }}>
                  {endDate ? format(endDate, "d MMM yyyy", { locale: nl }) : "Selecteer"}
                </Text>
                <Ionicons name="calendar-outline" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
          </View>

          {/* Date Pickers - Android */}
          {showStartPicker && Platform.OS === "android" && (
            <DateTimePicker
              value={startDate || new Date()}
              mode="date"
              display="default"
              onChange={(event, date) => {
                setShowStartPicker(false);
                if (event.type === "set" && date) {
                  setStartDate(date);
                  setHasChanges(true);
                }
              }}
            />
          )}
          {showEndPicker && Platform.OS === "android" && (
            <DateTimePicker
              value={endDate || new Date()}
              mode="date"
              display="default"
              onChange={(event, date) => {
                setShowEndPicker(false);
                if (event.type === "set" && date) {
                  setEndDate(date);
                  setHasChanges(true);
                }
              }}
            />
          )}

          {/* Date Pickers - iOS Modal */}
          {showStartPicker && Platform.OS === "ios" && (
            <Modal transparent animationType="fade">
              <View className="flex-1 justify-end" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                <View
                  className="rounded-t-2xl p-4"
                  style={{ backgroundColor: colors.surface }}
                >
                  <View className="flex-row justify-between mb-4">
                    <TouchableOpacity onPress={() => setShowStartPicker(false)}>
                      <Text style={{ color: colors.textSecondary }}>Annuleer</Text>
                    </TouchableOpacity>
                    <Text className="font-semibold" style={{ color: colors.text }}>
                      Startdatum
                    </Text>
                    <TouchableOpacity onPress={() => setShowStartPicker(false)}>
                      <Text className="font-semibold" style={{ color: "#10B981" }}>
                        Klaar
                      </Text>
                    </TouchableOpacity>
                  </View>
                  <DateTimePicker
                    value={startDate || new Date()}
                    mode="date"
                    display="spinner"
                    onChange={(event, date) => {
                      if (date) {
                        setStartDate(date);
                        setHasChanges(true);
                      }
                    }}
                    locale="nl-NL"
                  />
                </View>
              </View>
            </Modal>
          )}
          {showEndPicker && Platform.OS === "ios" && (
            <Modal transparent animationType="fade">
              <View className="flex-1 justify-end" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                <View
                  className="rounded-t-2xl p-4"
                  style={{ backgroundColor: colors.surface }}
                >
                  <View className="flex-row justify-between mb-4">
                    <TouchableOpacity onPress={() => setShowEndPicker(false)}>
                      <Text style={{ color: colors.textSecondary }}>Annuleer</Text>
                    </TouchableOpacity>
                    <Text className="font-semibold" style={{ color: colors.text }}>
                      Einddatum
                    </Text>
                    <TouchableOpacity onPress={() => setShowEndPicker(false)}>
                      <Text className="font-semibold" style={{ color: "#10B981" }}>
                        Klaar
                      </Text>
                    </TouchableOpacity>
                  </View>
                  <DateTimePicker
                    value={endDate || new Date()}
                    mode="date"
                    display="spinner"
                    onChange={(event, date) => {
                      if (date) {
                        setEndDate(date);
                        setHasChanges(true);
                      }
                    }}
                    locale="nl-NL"
                  />
                </View>
              </View>
            </Modal>
          )}

          {/* Location Header */}
          <View className="flex-row items-center mb-3 mt-2">
            <Ionicons name="location" size={16} color="#10B981" />
            <Text className="ml-2 text-sm font-semibold" style={{ color: "#10B981" }}>
              Weekend Locatie
            </Text>
          </View>

          {/* Location Name */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Naam locatie
            </Text>
            <TextInput
              value={locationName}
              onChangeText={(text) => {
                setLocationName(text);
                setHasChanges(true);
              }}
              placeholder="bijv. Vakantiepark De Eemhof"
              placeholderTextColor={colors.textTertiary}
              className="p-4 rounded-xl text-base"
              style={{
                backgroundColor: colors.background,
                color: colors.text,
                borderWidth: 1,
                borderColor: colors.border,
              }}
            />
          </View>

          {/* Location Address (Map Picker) */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Adres (kies op kaart)
            </Text>
            <LocationPicker
              value={locationAddress}
              onChange={(address, coordinates) => {
                setLocationAddress(address);
                if (coordinates) {
                  setLocationLat(coordinates.latitude);
                  setLocationLng(coordinates.longitude);
                }
                setHasChanges(true);
              }}
              placeholder="Tik om locatie te kiezen..."
            />
          </View>

          {/* Weekend Location Reveal Toggle */}
          {locationName.trim() && (
            <View className="mb-2">
              <TouchableOpacity
                onPress={() => {
                  setWeekendLocationRevealed(!weekendLocationRevealed);
                  setHasChanges(true);
                }}
                className="flex-row items-center justify-between p-4 rounded-xl"
                style={{
                  backgroundColor: weekendLocationRevealed ? "#DCFCE7" : colors.background,
                  borderWidth: 1,
                  borderColor: weekendLocationRevealed ? "#22C55E" : colors.border,
                }}
              >
                <View className="flex-row items-center flex-1">
                  <Ionicons
                    name={weekendLocationRevealed ? "eye" : "eye-off"}
                    size={20}
                    color={weekendLocationRevealed ? "#22C55E" : colors.textSecondary}
                  />
                  <View className="ml-3 flex-1">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      Locatie zichtbaar
                    </Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>
                      {weekendLocationRevealed
                        ? "Iedereen kan de locatie zien"
                        : "Locatie is verborgen voor gebruikers"}
                    </Text>
                  </View>
                </View>
                <View
                  className="w-12 h-6 rounded-full justify-center px-1"
                  style={{ backgroundColor: weekendLocationRevealed ? "#22C55E" : colors.border }}
                >
                  <View
                    className="w-4 h-4 rounded-full bg-white"
                    style={{ alignSelf: weekendLocationRevealed ? "flex-end" : "flex-start" }}
                  />
                </View>
              </TouchableOpacity>
            </View>
          )}

          {/* Instructies Header */}
          <View className="flex-row items-center justify-between mb-3 mt-4">
            <View className="flex-row items-center">
              <Ionicons name="list" size={16} color="#10B981" />
              <Text className="ml-2 text-sm font-semibold" style={{ color: "#10B981" }}>
                Instructies & Praktische Info
              </Text>
            </View>
            <TouchableOpacity
              onPress={() => {
                setEditingInstructiesType("weekend");
                setEditingInstructie(null);
                setInstructieTitle("");
                setInstructieDescription("");
                setInstructieIcon("information-circle");
                setShowInstructiesModal(true);
              }}
              className="flex-row items-center px-3 py-1.5 rounded-full"
              style={{ backgroundColor: "#10B98120" }}
            >
              <Ionicons name="add" size={16} color="#10B981" />
              <Text className="ml-1 text-sm font-medium" style={{ color: "#10B981" }}>
                Toevoegen
              </Text>
            </TouchableOpacity>
          </View>

          {/* Weekend Instructies List */}
          {weekendInstructies.length === 0 ? (
            <View
              className="p-4 rounded-xl items-center"
              style={{ backgroundColor: colors.background }}
            >
              <Ionicons name="document-text-outline" size={24} color={colors.textTertiary} />
              <Text className="mt-2 text-sm text-center" style={{ color: colors.textSecondary }}>
                Nog geen instructies toegevoegd
              </Text>
            </View>
          ) : (
            <View>
              {weekendInstructies.map((item, index) => (
                <View
                  key={item.id}
                  className="flex-row items-start p-3 rounded-xl mb-2"
                  style={{ backgroundColor: colors.background }}
                >
                  <View
                    className="w-8 h-8 rounded-full items-center justify-center mr-3"
                    style={{ backgroundColor: "#10B98120" }}
                  >
                    <Ionicons name={item.icon as any || "information-circle"} size={16} color="#10B981" />
                  </View>
                  <View className="flex-1">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      {item.title}
                    </Text>
                    <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                      {item.description}
                    </Text>
                  </View>
                  <View className="flex-row items-center">
                    <TouchableOpacity
                      onPress={() => {
                        setEditingInstructiesType("weekend");
                        setEditingInstructie(item);
                        setInstructieTitle(item.title);
                        setInstructieDescription(item.description);
                        setInstructieIcon(item.icon || "information-circle");
                        setShowInstructiesModal(true);
                      }}
                      className="p-2"
                    >
                      <Ionicons name="pencil" size={16} color={colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => {
                        Alert.alert(
                          "Verwijderen",
                          `Weet je zeker dat je "${item.title}" wilt verwijderen?`,
                          [
                            { text: "Annuleer", style: "cancel" },
                            {
                              text: "Verwijder",
                              style: "destructive",
                              onPress: () => {
                                setWeekendInstructies(prev => prev.filter(i => i.id !== item.id));
                                setHasChanges(true);
                              },
                            },
                          ]
                        );
                      }}
                      className="p-2"
                    >
                      <Ionicons name="trash-outline" size={16} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                </View>
              ))}
            </View>
          )}
        </View>

        {/* Familie Dag Section */}
        <View
          className="mb-6 p-4 rounded-2xl"
          style={{
            backgroundColor: colors.surface,
            borderWidth: 2,
            borderColor: "#F59E0B",
          }}
        >
          {/* Section Header */}
          <View className="flex-row items-center mb-4">
            <View
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: "#F59E0B20" }}
            >
              <Ionicons name="sunny" size={20} color="#F59E0B" />
            </View>
            <View>
              <Text className="text-lg font-bold" style={{ color: colors.text }}>
                Familie Dag
              </Text>
              <Text className="text-xs" style={{ color: colors.textSecondary }}>
                De jaarlijkse familie dag
              </Text>
            </View>
          </View>

          {/* Datum Header */}
          <View className="flex-row items-center mb-3">
            <Ionicons name="calendar" size={16} color="#F59E0B" />
            <Text className="ml-2 text-sm font-semibold" style={{ color: "#F59E0B" }}>
              Familie Dag Datum
            </Text>
          </View>

          {/* Familie Dag Date */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Datum
            </Text>
            <TouchableOpacity
              onPress={() => setShowFamilieDagPicker(true)}
              className="p-4 rounded-xl flex-row items-center justify-between"
              style={{
                backgroundColor: colors.background,
                borderWidth: 1,
                borderColor: colors.border,
              }}
            >
              <Text style={{ color: familieDagDate ? colors.text : colors.textTertiary }}>
                {familieDagDate ? format(familieDagDate, "d MMMM yyyy", { locale: nl }) : "Selecteer datum"}
              </Text>
              <Ionicons name="calendar-outline" size={20} color="#F59E0B" />
            </TouchableOpacity>
          </View>

          {showFamilieDagPicker && Platform.OS === "android" && (
            <DateTimePicker
              value={familieDagDate || new Date()}
              mode="date"
              display="default"
              onChange={(event, date) => {
                setShowFamilieDagPicker(false);
                if (event.type === "set" && date) {
                  setFamilieDagDate(date);
                  setHasChanges(true);
                }
              }}
            />
          )}

          {/* iOS Date Picker Modal */}
          {showFamilieDagPicker && Platform.OS === "ios" && (
            <Modal transparent animationType="fade">
              <View className="flex-1 justify-end" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                <View
                  className="rounded-t-2xl p-4"
                  style={{ backgroundColor: colors.surface }}
                >
                  <View className="flex-row justify-between mb-4">
                    <TouchableOpacity onPress={() => setShowFamilieDagPicker(false)}>
                      <Text style={{ color: colors.textSecondary }}>Annuleer</Text>
                    </TouchableOpacity>
                    <Text className="font-semibold" style={{ color: colors.text }}>
                      Familie Dag Datum
                    </Text>
                    <TouchableOpacity onPress={() => setShowFamilieDagPicker(false)}>
                      <Text className="font-semibold" style={{ color: "#F59E0B" }}>
                        Klaar
                      </Text>
                    </TouchableOpacity>
                  </View>
                  <DateTimePicker
                    value={familieDagDate || new Date()}
                    mode="date"
                    display="spinner"
                    onChange={(event, date) => {
                      if (date) {
                        setFamilieDagDate(date);
                        setHasChanges(true);
                      }
                    }}
                    locale="nl-NL"
                  />
                </View>
              </View>
            </Modal>
          )}

          {/* Location Header */}
          <View className="flex-row items-center mb-3 mt-2">
            <Ionicons name="location" size={16} color="#F59E0B" />
            <Text className="ml-2 text-sm font-semibold" style={{ color: "#F59E0B" }}>
              Familie Dag Locatie
            </Text>
          </View>

          {/* Familie Dag Location Name */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Naam locatie
            </Text>
            <TextInput
              value={familieDagLocationName}
              onChangeText={(text) => {
                setFamilieDagLocationName(text);
                setFamilieDagLocation(text); // Keep legacy field in sync
                setHasChanges(true);
              }}
              placeholder="bijv. Bij Oma & Opa thuis"
              placeholderTextColor={colors.textTertiary}
              className="p-4 rounded-xl text-base"
              style={{
                backgroundColor: colors.background,
                color: colors.text,
                borderWidth: 1,
                borderColor: colors.border,
              }}
            />
          </View>

          {/* Familie Dag Location Address (Map Picker) */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Adres (kies op kaart)
            </Text>
            <LocationPicker
              value={familieDagLocationAddress}
              onChange={(address, coordinates) => {
                setFamilieDagLocationAddress(address);
                if (coordinates) {
                  setFamilieDagLocationLat(coordinates.latitude);
                  setFamilieDagLocationLng(coordinates.longitude);
                }
                setHasChanges(true);
              }}
              placeholder="Tik om locatie te kiezen..."
            />
          </View>

          {/* Familie Dag Location Reveal Toggle */}
          {(familieDagLocationName.trim() || familieDagLocationAddress.trim()) && (
            <View className="mb-2">
              <TouchableOpacity
                onPress={() => {
                  setFamilieDagLocationRevealed(!familieDagLocationRevealed);
                  setHasChanges(true);
                }}
                className="flex-row items-center justify-between p-4 rounded-xl"
                style={{
                  backgroundColor: familieDagLocationRevealed ? "#DCFCE7" : colors.background,
                  borderWidth: 1,
                  borderColor: familieDagLocationRevealed ? "#22C55E" : colors.border,
                }}
              >
                <View className="flex-row items-center flex-1">
                  <Ionicons
                    name={familieDagLocationRevealed ? "eye" : "eye-off"}
                    size={20}
                    color={familieDagLocationRevealed ? "#22C55E" : colors.textSecondary}
                  />
                  <View className="ml-3 flex-1">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      Locatie zichtbaar
                    </Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>
                      {familieDagLocationRevealed
                        ? "Iedereen kan de locatie zien"
                        : "Locatie is verborgen voor gebruikers"}
                    </Text>
                  </View>
                </View>
                <View
                  className="w-12 h-6 rounded-full justify-center px-1"
                  style={{ backgroundColor: familieDagLocationRevealed ? "#22C55E" : colors.border }}
                >
                  <View
                    className="w-4 h-4 rounded-full bg-white"
                    style={{ alignSelf: familieDagLocationRevealed ? "flex-end" : "flex-start" }}
                  />
                </View>
              </TouchableOpacity>
            </View>
          )}

          {/* Instructies Header */}
          <View className="flex-row items-center justify-between mb-3 mt-4">
            <View className="flex-row items-center">
              <Ionicons name="list" size={16} color="#F59E0B" />
              <Text className="ml-2 text-sm font-semibold" style={{ color: "#F59E0B" }}>
                Instructies & Praktische Info
              </Text>
            </View>
            <TouchableOpacity
              onPress={() => {
                setEditingInstructiesType("familiedag");
                setEditingInstructie(null);
                setInstructieTitle("");
                setInstructieDescription("");
                setInstructieIcon("information-circle");
                setShowInstructiesModal(true);
              }}
              className="flex-row items-center px-3 py-1.5 rounded-full"
              style={{ backgroundColor: "#F59E0B20" }}
            >
              <Ionicons name="add" size={16} color="#F59E0B" />
              <Text className="ml-1 text-sm font-medium" style={{ color: "#F59E0B" }}>
                Toevoegen
              </Text>
            </TouchableOpacity>
          </View>

          {/* Familie Dag Instructies List */}
          {familieDagInstructies.length === 0 ? (
            <View
              className="p-4 rounded-xl items-center"
              style={{ backgroundColor: colors.background }}
            >
              <Ionicons name="document-text-outline" size={24} color={colors.textTertiary} />
              <Text className="mt-2 text-sm text-center" style={{ color: colors.textSecondary }}>
                Nog geen instructies toegevoegd
              </Text>
            </View>
          ) : (
            <View>
              {familieDagInstructies.map((item, index) => (
                <View
                  key={item.id}
                  className="flex-row items-start p-3 rounded-xl mb-2"
                  style={{ backgroundColor: colors.background }}
                >
                  <View
                    className="w-8 h-8 rounded-full items-center justify-center mr-3"
                    style={{ backgroundColor: "#F59E0B20" }}
                  >
                    <Ionicons name={item.icon as any || "information-circle"} size={16} color="#F59E0B" />
                  </View>
                  <View className="flex-1">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      {item.title}
                    </Text>
                    <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                      {item.description}
                    </Text>
                  </View>
                  <View className="flex-row items-center">
                    <TouchableOpacity
                      onPress={() => {
                        setEditingInstructiesType("familiedag");
                        setEditingInstructie(item);
                        setInstructieTitle(item.title);
                        setInstructieDescription(item.description);
                        setInstructieIcon(item.icon || "information-circle");
                        setShowInstructiesModal(true);
                      }}
                      className="p-2"
                    >
                      <Ionicons name="pencil" size={16} color={colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => {
                        Alert.alert(
                          "Verwijderen",
                          `Weet je zeker dat je "${item.title}" wilt verwijderen?`,
                          [
                            { text: "Annuleer", style: "cancel" },
                            {
                              text: "Verwijder",
                              style: "destructive",
                              onPress: () => {
                                setFamilieDagInstructies(prev => prev.filter(i => i.id !== item.id));
                                setHasChanges(true);
                              },
                            },
                          ]
                        );
                      }}
                      className="p-2"
                    >
                      <Ionicons name="trash-outline" size={16} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                </View>
              ))}
            </View>
          )}
        </View>

        {/* Tip about reveal via challenge */}
        {(familieDagLocation.trim() || locationName.trim()) && (!familieDagLocationRevealed || !weekendLocationRevealed) && (
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.primaryLight }}
          >
            <View className="flex-row items-center mb-2">
              <Ionicons name="bulb" size={18} color={colors.primary} />
              <Text className="ml-2 font-medium" style={{ color: colors.primary }}>
                Tip: Onthul via uitdaging
              </Text>
            </View>
            <Text className="text-sm" style={{ color: colors.textSecondary }}>
              Je kunt de locatie ook automatisch onthullen wanneer een uitdaging wordt voltooid.
              Maak hiervoor een uitdaging aan en koppel deze aan het evenement.
            </Text>
          </View>
        )}

        {/* Banner Color */}
        <View className="mb-6">
          <Text className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: colors.textTertiary }}>
            Banner Kleur
          </Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View className="flex-row">
              {COLOR_OPTIONS.map((color) => (
                <TouchableOpacity
                  key={color.id}
                  onPress={() => {
                    setBannerColor(color.id);
                    setHasChanges(true);
                  }}
                  className="items-center mr-4"
                >
                  <View
                    className="w-12 h-12 rounded-full items-center justify-center"
                    style={{
                      backgroundColor: color.id,
                      borderWidth: bannerColor === color.id ? 3 : 0,
                      borderColor: colors.text,
                    }}
                  >
                    {bannerColor === color.id && (
                      <Ionicons name="checkmark" size={24} color="#FFFFFF" />
                    )}
                  </View>
                  <Text className="text-xs mt-1" style={{ color: colors.textSecondary }}>
                    {color.name}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
        </View>

        {/* Lottie Animation */}
        <View className="mb-6">
          <Text className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: colors.textTertiary }}>
            Banner Animatie
          </Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View className="flex-row">
              {LOTTIE_OPTIONS.map((lottie) => (
                <TouchableOpacity
                  key={lottie.id}
                  onPress={() => {
                    setLottieAnimation(lottie.id);
                    setHasChanges(true);
                  }}
                  className="items-center mr-4"
                >
                  <View
                    className="w-20 h-20 rounded-xl items-center justify-center"
                    style={{
                      backgroundColor: colors.surface,
                      borderWidth: lottieAnimation === lottie.id ? 2 : 1,
                      borderColor: lottieAnimation === lottie.id ? bannerColor : colors.border,
                    }}
                  >
                    <LottieView
                      source={lottie.file}
                      autoPlay
                      loop
                      style={{ width: 50, height: 50 }}
                    />
                  </View>
                  <Text
                    className="text-xs mt-1 text-center"
                    style={{ color: lottieAnimation === lottie.id ? bannerColor : colors.textSecondary }}
                  >
                    {lottie.name}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
        </View>

        {/* Competition Settings */}
        <View
          className="mb-6 p-4 rounded-2xl"
          style={{
            backgroundColor: colors.surface,
            borderWidth: 2,
            borderColor: "#8B5CF6",
          }}
        >
          {/* Section Header */}
          <View className="flex-row items-center mb-4">
            <View
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: "#8B5CF620" }}
            >
              <Ionicons name="trophy" size={20} color="#8B5CF6" />
            </View>
            <View className="flex-1">
              <Text className="text-lg font-bold" style={{ color: colors.text }}>
                Spellen Competitie
              </Text>
              <Text className="text-xs" style={{ color: colors.textSecondary }}>
                Wanneer kunnen teams punten verdienen?
              </Text>
            </View>
          </View>

          {/* Competition Period Info */}
          <View
            className="p-3 rounded-xl mb-4"
            style={{ backgroundColor: "#8B5CF610" }}
          >
            <View className="flex-row items-start">
              <Ionicons name="information-circle" size={18} color="#8B5CF6" />
              <Text className="ml-2 text-sm flex-1" style={{ color: colors.textSecondary }}>
                Stel de periode in waarin teams punten kunnen verdienen. Per spel kun je in{" "}
                <Text style={{ color: "#8B5CF6", fontWeight: "600" }}>
                  Spellen Beheren
                </Text>
                {" "}optioneel een eigen periode instellen.
              </Text>
            </View>
          </View>

          {/* Global Competition Period - Always Required */}
          <>
              <View className="flex-row items-center mb-3 mt-2">
                <Ionicons name="calendar" size={16} color="#8B5CF6" />
                <Text className="ml-2 text-sm font-semibold" style={{ color: "#8B5CF6" }}>
                  Competitie Periode
                </Text>
              </View>

              <View className="flex-row mb-4">
                <View className="flex-1 mr-2">
                  <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                    Startdatum
                  </Text>
                  <TouchableOpacity
                    onPress={() => setShowCompetitionStartPicker(true)}
                    className="p-4 rounded-xl flex-row items-center justify-between"
                    style={{
                      backgroundColor: colors.background,
                      borderWidth: 1,
                      borderColor: colors.border,
                    }}
                  >
                    <Text style={{ color: competitionStartsAt ? colors.text : colors.textTertiary }}>
                      {competitionStartsAt
                        ? format(competitionStartsAt, "d MMM HH:mm", { locale: nl })
                        : "Selecteer"}
                    </Text>
                    <Ionicons name="calendar-outline" size={20} color="#8B5CF6" />
                  </TouchableOpacity>
                </View>
                <View className="flex-1 ml-2">
                  <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                    Einddatum
                  </Text>
                  <TouchableOpacity
                    onPress={() => setShowCompetitionEndPicker(true)}
                    className="p-4 rounded-xl flex-row items-center justify-between"
                    style={{
                      backgroundColor: colors.background,
                      borderWidth: 1,
                      borderColor: colors.border,
                    }}
                  >
                    <Text style={{ color: competitionEndsAt ? colors.text : colors.textTertiary }}>
                      {competitionEndsAt
                        ? format(competitionEndsAt, "d MMM HH:mm", { locale: nl })
                        : "Selecteer"}
                    </Text>
                    <Ionicons name="calendar-outline" size={20} color="#8B5CF6" />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Competition Date+Time Pickers - Android (two-step: date then time) */}
              {showCompetitionStartPicker && Platform.OS === "android" && (
                <DateTimePicker
                  value={competitionStartsAt || new Date()}
                  mode="date"
                  display="default"
                  onChange={(event, date) => {
                    setShowCompetitionStartPicker(false);
                    if (event.type === "set" && date) {
                      setAndroidTempDate(date);
                      setAndroidTimePickerTarget("start");
                    }
                  }}
                />
              )}
              {showCompetitionEndPicker && Platform.OS === "android" && (
                <DateTimePicker
                  value={competitionEndsAt || new Date()}
                  mode="date"
                  display="default"
                  onChange={(event, date) => {
                    setShowCompetitionEndPicker(false);
                    if (event.type === "set" && date) {
                      setAndroidTempDate(date);
                      setAndroidTimePickerTarget("end");
                    }
                  }}
                />
              )}
              {androidTimePickerTarget !== null && Platform.OS === "android" && androidTempDate && (
                <DateTimePicker
                  value={androidTempDate}
                  mode="time"
                  is24Hour
                  display="default"
                  onChange={(event, date) => {
                    const target = androidTimePickerTarget;
                    setAndroidTimePickerTarget(null);
                    if (event.type === "set" && date) {
                      const combined = new Date(androidTempDate);
                      combined.setHours(date.getHours(), date.getMinutes(), 0, 0);
                      if (target === "start") {
                        setCompetitionStartsAt(combined);
                      } else {
                        setCompetitionEndsAt(combined);
                      }
                      setHasChanges(true);
                    }
                    setAndroidTempDate(null);
                  }}
                />
              )}

              {/* iOS Date Picker Modal */}
              {showCompetitionStartPicker && Platform.OS === "ios" && (
                <Modal transparent animationType="fade">
                  <View className="flex-1 justify-end" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                    <View
                      className="rounded-t-2xl p-4"
                      style={{ backgroundColor: colors.surface }}
                    >
                      <View className="flex-row justify-between mb-4">
                        <TouchableOpacity onPress={() => setShowCompetitionStartPicker(false)}>
                          <Text style={{ color: colors.textSecondary }}>Annuleer</Text>
                        </TouchableOpacity>
                        <Text className="font-semibold" style={{ color: colors.text }}>
                          Competitie Start
                        </Text>
                        <TouchableOpacity onPress={() => setShowCompetitionStartPicker(false)}>
                          <Text className="font-semibold" style={{ color: "#8B5CF6" }}>
                            Klaar
                          </Text>
                        </TouchableOpacity>
                      </View>
                      <DateTimePicker
                        value={competitionStartsAt || new Date()}
                        mode="datetime"
                        display="spinner"
                        onChange={(event, date) => {
                          if (date) {
                            setCompetitionStartsAt(date);
                            setHasChanges(true);
                          }
                        }}
                        locale="nl-NL"
                      />
                    </View>
                  </View>
                </Modal>
              )}
              {showCompetitionEndPicker && Platform.OS === "ios" && (
                <Modal transparent animationType="fade">
                  <View className="flex-1 justify-end" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                    <View
                      className="rounded-t-2xl p-4"
                      style={{ backgroundColor: colors.surface }}
                    >
                      <View className="flex-row justify-between mb-4">
                        <TouchableOpacity onPress={() => setShowCompetitionEndPicker(false)}>
                          <Text style={{ color: colors.textSecondary }}>Annuleer</Text>
                        </TouchableOpacity>
                        <Text className="font-semibold" style={{ color: colors.text }}>
                          Competitie Einde
                        </Text>
                        <TouchableOpacity onPress={() => setShowCompetitionEndPicker(false)}>
                          <Text className="font-semibold" style={{ color: "#8B5CF6" }}>
                            Klaar
                          </Text>
                        </TouchableOpacity>
                      </View>
                      <DateTimePicker
                        value={competitionEndsAt || new Date()}
                        mode="datetime"
                        display="spinner"
                        onChange={(event, date) => {
                          if (date) {
                            setCompetitionEndsAt(date);
                            setHasChanges(true);
                          }
                        }}
                        locale="nl-NL"
                      />
                    </View>
                  </View>
                </Modal>
              )}
            </>

          {/* Leaderboard Toggle */}
          <TouchableOpacity
            onPress={() => {
              setShowLeaderboard(!showLeaderboard);
              setHasChanges(true);
            }}
            className="flex-row items-center justify-between p-4 rounded-xl"
            style={{
              backgroundColor: showLeaderboard ? "#8B5CF610" : colors.background,
              borderWidth: 1,
              borderColor: showLeaderboard ? "#8B5CF6" : colors.border,
            }}
          >
            <View className="flex-row items-center flex-1">
              <Ionicons
                name="podium"
                size={20}
                color={showLeaderboard ? "#8B5CF6" : colors.textSecondary}
              />
              <View className="ml-3 flex-1">
                <Text className="font-medium" style={{ color: colors.text }}>
                  Ranglijst Tonen
                </Text>
                <Text className="text-xs" style={{ color: colors.textSecondary }}>
                  {showLeaderboard
                    ? "Gebruikers kunnen de ranglijst zien"
                    : "Ranglijst is verborgen"}
                </Text>
              </View>
            </View>
            <View
              className="w-12 h-6 rounded-full justify-center px-1"
              style={{ backgroundColor: showLeaderboard ? "#8B5CF6" : colors.border }}
            >
              <View
                className="w-4 h-4 rounded-full bg-white"
                style={{ alignSelf: showLeaderboard ? "flex-end" : "flex-start" }}
              />
            </View>
          </TouchableOpacity>
        </View>

        {/* Familie Chat Section */}
        <View
          className="mb-6 p-4 rounded-2xl"
          style={{
            backgroundColor: colors.surface,
            borderWidth: 2,
            borderColor: "#7C3AED",
          }}
        >
          {/* Section Header */}
          <View className="flex-row items-center mb-4">
            <View
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: "#7C3AED20" }}
            >
              <Ionicons name="chatbubbles" size={20} color="#7C3AED" />
            </View>
            <View className="flex-1">
              <Text className="text-lg font-bold" style={{ color: colors.text }}>
                Familie Chat
              </Text>
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                Groepschat voor alle familieleden
              </Text>
            </View>
          </View>

          {familyChatId ? (
            // Chat exists
            <View>
              <View
                className="flex-row items-center p-4 rounded-xl mb-3"
                style={{ backgroundColor: "#10B98120" }}
              >
                <Ionicons name="checkmark-circle" size={24} color="#10B981" />
                <View className="ml-3 flex-1">
                  <Text className="font-medium" style={{ color: "#10B981" }}>
                    Chat is actief
                  </Text>
                  <Text className="text-sm" style={{ color: colors.textSecondary }}>
                    {familyChatParticipantCount} deelnemers
                  </Text>
                </View>
              </View>
              
              <TouchableOpacity
                onPress={handleOpenFamilyChat}
                className="flex-row items-center justify-center py-3 rounded-xl"
                style={{ backgroundColor: "#7C3AED" }}
              >
                <Ionicons name="chatbubbles" size={20} color="#FFFFFF" />
                <Text className="text-white font-semibold ml-2">
                  Open Familie Chat
                </Text>
              </TouchableOpacity>
            </View>
          ) : (
            // Chat doesn't exist
            <View>
              <View
                className="flex-row items-center p-4 rounded-xl mb-3"
                style={{ backgroundColor: "#F59E0B20" }}
              >
                <Ionicons name="information-circle" size={24} color="#F59E0B" />
                <View className="ml-3 flex-1">
                  <Text className="font-medium" style={{ color: "#F59E0B" }}>
                    Nog geen familie chat
                  </Text>
                  <Text className="text-sm" style={{ color: colors.textSecondary }}>
                    Maak een groepschat voor dit weekend
                  </Text>
                </View>
              </View>
              
              <TouchableOpacity
                onPress={handleCreateFamilyChat}
                disabled={isCreatingChat}
                className="flex-row items-center justify-center py-3 rounded-xl"
                style={{ 
                  backgroundColor: isCreatingChat ? "#A78BFA" : "#7C3AED",
                  opacity: isCreatingChat ? 0.7 : 1,
                }}
              >
                {isCreatingChat ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <>
                    <Ionicons name="add-circle" size={20} color="#FFFFFF" />
                    <Text className="text-white font-semibold ml-2">
                      Familie Chat Aanmaken
                    </Text>
                  </>
                )}
              </TouchableOpacity>

              <Text className="text-xs text-center mt-3" style={{ color: colors.textTertiary }}>
                Alle gebruikers met een account worden automatisch toegevoegd
              </Text>
            </View>
          )}
        </View>

        {/* Familie Admins */}
        <View className="mb-6">
          <Text className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: colors.textTertiary }}>
            Familie Admins ({familieAdmins.length})
          </Text>
          <View
            className="rounded-xl p-4"
            style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
          >
            {familieAdmins.length === 0 ? (
              <Text style={{ color: colors.textSecondary }}>Geen familie admins gevonden</Text>
            ) : (
              familieAdmins.map((admin, index) => (
                <View
                  key={admin.id}
                  className={`flex-row items-center ${index > 0 ? "mt-3 pt-3 border-t" : ""}`}
                  style={{ borderColor: colors.border }}
                >
                  {admin.avatar_url ? (
                    <Image
                      source={{ uri: admin.avatar_url }}
                      className="w-10 h-10 rounded-full mr-3"
                    />
                  ) : (
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center mr-3"
                      style={{ backgroundColor: colors.primaryLight }}
                    >
                      <Text className="font-bold" style={{ color: colors.primary }}>
                        {admin.full_name?.charAt(0).toUpperCase() || "?"}
                      </Text>
                    </View>
                  )}
                  <View className="flex-1">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      {admin.full_name || "Onbekend"}
                    </Text>
                    <Text className="text-sm" style={{ color: colors.textSecondary }}>
                      {admin.email}
                    </Text>
                  </View>
                  <View
                    className="px-2 py-1 rounded-full"
                    style={{ backgroundColor: "#FEF3C7" }}
                  >
                    <Text className="text-xs font-medium" style={{ color: "#B45309" }}>
                      Admin
                    </Text>
                  </View>
                </View>
              ))
            )}
          </View>
        </View>

        {/* Save Button */}
        <TouchableOpacity
          onPress={handleSave}
          disabled={isSaving || !hasChanges}
          className="py-4 rounded-xl items-center mb-8"
          style={{
            backgroundColor: hasChanges ? "#D97706" : colors.border,
            opacity: isSaving ? 0.7 : 1,
          }}
        >
          {isSaving ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text className="text-white font-semibold text-base">
              {hasChanges ? "Opslaan" : "Geen wijzigingen"}
            </Text>
          )}
        </TouchableOpacity>
      </ScrollView>

      {/* Instructies Modal */}
      <Modal visible={showInstructiesModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          className="flex-1"
        >
          {/* Modal Header */}
          <View
            className="flex-row items-center justify-between px-4 py-3"
            style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
          >
            <TouchableOpacity
              onPress={() => {
                setShowInstructiesModal(false);
                setEditingInstructie(null);
              }}
            >
              <Text style={{ color: colors.textSecondary }}>Annuleren</Text>
            </TouchableOpacity>
            <Text className="font-semibold" style={{ color: colors.text }}>
              {editingInstructie ? "Instructie bewerken" : "Nieuwe instructie"}
            </Text>
            <TouchableOpacity
              onPress={() => {
                if (!instructieTitle.trim() || !instructieDescription.trim()) {
                  Alert.alert("Fout", "Titel en beschrijving zijn verplicht");
                  return;
                }

                const newInstructie: InstructieItem = {
                  id: editingInstructie?.id || `instr-${Date.now()}`,
                  title: instructieTitle.trim(),
                  description: instructieDescription.trim(),
                  icon: instructieIcon,
                  order: editingInstructie?.order || (editingInstructiesType === "weekend" ? weekendInstructies.length : familieDagInstructies.length),
                };

                if (editingInstructiesType === "weekend") {
                  if (editingInstructie) {
                    setWeekendInstructies(prev => prev.map(i => i.id === editingInstructie.id ? newInstructie : i));
                  } else {
                    setWeekendInstructies(prev => [...prev, newInstructie]);
                  }
                } else {
                  if (editingInstructie) {
                    setFamilieDagInstructies(prev => prev.map(i => i.id === editingInstructie.id ? newInstructie : i));
                  } else {
                    setFamilieDagInstructies(prev => [...prev, newInstructie]);
                  }
                }

                setHasChanges(true);
                setShowInstructiesModal(false);
                setEditingInstructie(null);
              }}
            >
              <Text className="font-semibold" style={{ color: colors.primary }}>
                Opslaan
              </Text>
            </TouchableOpacity>
          </View>

          <ScrollView className="flex-1 px-4 py-6" showsVerticalScrollIndicator={false}>
            {/* Type Indicator */}
            <View
              className="flex-row items-center px-3 py-2 rounded-lg mb-6"
              style={{
                backgroundColor: editingInstructiesType === "weekend" ? "#10B98120" : "#F59E0B20",
              }}
            >
              <Ionicons
                name={editingInstructiesType === "weekend" ? "bed" : "sunny"}
                size={16}
                color={editingInstructiesType === "weekend" ? "#10B981" : "#F59E0B"}
              />
              <Text
                className="ml-2 text-sm font-medium"
                style={{ color: editingInstructiesType === "weekend" ? "#10B981" : "#F59E0B" }}
              >
                {editingInstructiesType === "weekend" ? "Familie Weekend" : "Familie Dag"}
              </Text>
            </View>

            {/* Title */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Titel *
              </Text>
              <TextInput
                value={instructieTitle}
                onChangeText={setInstructieTitle}
                placeholder="bijv. Inchecken"
                placeholderTextColor={colors.textTertiary}
                className="p-4 rounded-xl text-base"
                style={{
                  backgroundColor: colors.surface,
                  color: colors.text,
                  borderWidth: 1,
                  borderColor: colors.border,
                }}
              />
            </View>

            {/* Description */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Beschrijving *
              </Text>
              <TextInput
                value={instructieDescription}
                onChangeText={setInstructieDescription}
                placeholder="bijv. Inchecken kan vanaf 15:00 uur bij de receptie"
                placeholderTextColor={colors.textTertiary}
                multiline
                numberOfLines={4}
                textAlignVertical="top"
                className="p-4 rounded-xl text-base"
                style={{
                  backgroundColor: colors.surface,
                  color: colors.text,
                  borderWidth: 1,
                  borderColor: colors.border,
                  minHeight: 100,
                }}
              />
            </View>

            {/* Icon */}
            <View className="mb-6">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Icoon
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View className="flex-row">
                  {[
                    { id: "information-circle", name: "Info" },
                    { id: "time", name: "Tijd" },
                    { id: "location", name: "Locatie" },
                    { id: "car", name: "Auto" },
                    { id: "bed", name: "Slapen" },
                    { id: "restaurant", name: "Eten" },
                    { id: "call", name: "Bellen" },
                    { id: "key", name: "Sleutel" },
                    { id: "wifi", name: "WiFi" },
                    { id: "card", name: "Betalen" },
                    { id: "medical", name: "Medisch" },
                    { id: "warning", name: "Let op" },
                    { id: "checkmark-circle", name: "Check" },
                    { id: "gift", name: "Cadeau" },
                    { id: "shirt", name: "Kleding" },
                    { id: "walk", name: "Wandelen" },
                  ].map((icon) => (
                    <TouchableOpacity
                      key={icon.id}
                      onPress={() => setInstructieIcon(icon.id)}
                      className="items-center mr-4"
                    >
                      <View
                        className="w-12 h-12 rounded-full items-center justify-center"
                        style={{
                          backgroundColor: instructieIcon === icon.id ? colors.primary : colors.surface,
                          borderWidth: instructieIcon === icon.id ? 0 : 1,
                          borderColor: colors.border,
                        }}
                      >
                        <Ionicons
                          name={icon.id as any}
                          size={22}
                          color={instructieIcon === icon.id ? "#FFFFFF" : colors.textSecondary}
                        />
                      </View>
                      <Text
                        className="text-xs mt-1"
                        style={{
                          color: instructieIcon === icon.id ? colors.primary : colors.textSecondary,
                        }}
                      >
                        {icon.name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Preview */}
            <View className="mb-6">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Voorbeeld
              </Text>
              <View
                className="flex-row items-start p-4 rounded-xl"
                style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
              >
                <View
                  className="w-10 h-10 rounded-full items-center justify-center mr-3"
                  style={{
                    backgroundColor: editingInstructiesType === "weekend" ? "#10B98120" : "#F59E0B20",
                  }}
                >
                  <Ionicons
                    name={instructieIcon as any}
                    size={20}
                    color={editingInstructiesType === "weekend" ? "#10B981" : "#F59E0B"}
                  />
                </View>
                <View className="flex-1">
                  <Text className="font-medium" style={{ color: colors.text }}>
                    {instructieTitle || "Titel"}
                  </Text>
                  <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                    {instructieDescription || "Beschrijving"}
                  </Text>
                </View>
              </View>
            </View>

            <View className="h-8" />
          </ScrollView>
        </KeyboardAvoidingView>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

